import { Value } from '@sinclair/typebox/value'
import type { NormalizedSchema, ValidationResult } from './types'

function isZodSchema(schema: any): boolean {
  return (
    schema != null &&
    typeof schema === 'object' &&
    typeof schema.safeParse === 'function' &&
    '_def' in schema
  )
}

// ---------------------------------------------------------------------------
// Zod normalization (Zod v4)
// ---------------------------------------------------------------------------

function normalizeZodField(schema: any, optional = false): NormalizedSchema {
  const def = schema._def
  const type: string = def.type

  if (type === 'optional' || type === 'nullable' || type === 'default' || type === 'prefault') {
    return normalizeZodField(def.innerType, true)
  }

  if (type === 'string') {
    return { kind: 'string', optional, format: def.format ?? undefined, originalSchema: schema, schemaKind: 'zod' }
  }

  if (type === 'number' || type === 'int' || type === 'integer') {
    return { kind: 'number', optional, originalSchema: schema, schemaKind: 'zod' }
  }

  if (type === 'boolean') {
    return { kind: 'boolean', optional, originalSchema: schema, schemaKind: 'zod' }
  }

  if (type === 'enum') {
    const entries: Record<string, string> = def.entries ?? {}
    return {
      kind: 'enum',
      optional,
      enumValues: Object.entries(entries).map(([k, v]) => ({ value: String(k), label: String(v) })),
      originalSchema: schema,
      schemaKind: 'zod',
    }
  }

  if (type === 'array') {
    return {
      kind: 'array',
      optional,
      items: def.element ? normalizeZodField(def.element) : undefined,
      originalSchema: schema,
      schemaKind: 'zod',
    }
  }

  if (type === 'object') {
    const shape: Record<string, any> = def.shape ?? {}
    const properties: Record<string, NormalizedSchema> = {}
    const requiredFields: string[] = []

    for (const [key, fieldSchema] of Object.entries(shape)) {
      const fieldDef = (fieldSchema as any)._def
      const isOptional =
        fieldDef.type === 'optional' ||
        fieldDef.type === 'nullable' ||
        fieldDef.type === 'default' ||
        fieldDef.type === 'prefault'
      properties[key] = normalizeZodField(fieldSchema as any, isOptional)
      if (!isOptional) requiredFields.push(key)
    }

    return { kind: 'object', optional, properties, requiredFields, originalSchema: schema, schemaKind: 'zod' }
  }

  return { kind: 'unknown', optional, originalSchema: schema, schemaKind: 'zod' }
}

// ---------------------------------------------------------------------------
// TypeBox / plain JSON Schema normalization
// ---------------------------------------------------------------------------

function normalizeTypeBoxField(schema: any, required = true): NormalizedSchema {
  const type: string | undefined = schema.type

  if (schema.enum) {
    return {
      kind: 'enum',
      optional: !required,
      enumValues: (schema.enum as any[]).map((v) => ({ value: String(v), label: String(v) })),
      originalSchema: schema,
      schemaKind: 'typebox',
    }
  }

  // Union of literals (TypeBox Union([Literal('a'), Literal('b')]))
  if (schema.anyOf && Array.isArray(schema.anyOf) && schema.anyOf.every((s: any) => 'const' in s)) {
    return {
      kind: 'enum',
      optional: !required,
      enumValues: schema.anyOf.map((s: any) => ({ value: String(s.const), label: String(s.const) })),
      originalSchema: schema,
      schemaKind: 'typebox',
    }
  }

  if (type === 'string') {
    return { kind: 'string', optional: !required, format: schema.format, originalSchema: schema, schemaKind: 'typebox' }
  }

  if (type === 'number' || type === 'integer') {
    return { kind: 'number', optional: !required, originalSchema: schema, schemaKind: 'typebox' }
  }

  if (type === 'boolean') {
    return { kind: 'boolean', optional: !required, originalSchema: schema, schemaKind: 'typebox' }
  }

  if (type === 'array') {
    return {
      kind: 'array',
      optional: !required,
      items: schema.items ? normalizeTypeBoxField(schema.items) : undefined,
      originalSchema: schema,
      schemaKind: 'typebox',
    }
  }

  if (type === 'object') {
    const props: Record<string, any> = schema.properties ?? {}
    const requiredFields: string[] = schema.required ?? []
    const properties: Record<string, NormalizedSchema> = {}
    for (const [key, propSchema] of Object.entries(props)) {
      properties[key] = normalizeTypeBoxField(propSchema as any, requiredFields.includes(key))
    }
    return { kind: 'object', optional: !required, properties, requiredFields, originalSchema: schema, schemaKind: 'typebox' }
  }

  return { kind: 'unknown', optional: !required, originalSchema: schema, schemaKind: 'typebox' }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function normalizeSchema(schema: any): NormalizedSchema {
  if (isZodSchema(schema)) return normalizeZodField(schema)
  return normalizeTypeBoxField(schema)
}

export function validateValue(normalized: NormalizedSchema, value: any): ValidationResult {
  const schema = normalized.originalSchema

  if (normalized.schemaKind === 'zod') {
    const result = schema.safeParse(value)
    if (result.success) return { isValid: true, errors: [] }
    return {
      isValid: false,
      errors: (result.error.issues ?? []).map((issue: any) => ({
        path: issue.path ?? [],
        message: issue.message ?? 'Invalid value',
      })),
    }
  }

  // TypeBox / JSON Schema
  try {
    const errors = [...Value.Errors(schema, value)].map((e: any) => ({
      path: String(e.path)
        .split('/')
        .filter(Boolean),
      message: e.message as string,
    }))
    return { isValid: errors.length === 0, errors }
  } catch {
    return { isValid: true, errors: [] }
  }
}

/** Extract the errors at a specific path from a ValidationResult */
export function errorsAtPath(result: ValidationResult, path: (string | number)[]): string[] {
  const pathStr = path.join('.')
  return result.errors
    .filter((e) => {
      const ePathStr = e.path.join('.')
      return ePathStr === pathStr || ePathStr.startsWith(pathStr + '.')
    })
    .map((e) => e.message)
}
